import { Landmark, Building2, Globe2, Newspaper, Siren, Briefcase, GraduationCap, BadgeCheck, Crown } from "lucide-react";
import { cn } from "@/lib/utils";

export type AccountType =
  | "citizen" | "government" | "municipality" | "ngo"
  | "journalist" | "emergency" | "business" | "research";

type Meta = {
  label: string;
  short: string;
  icon: React.ComponentType<{ className?: string }>;
  className: string; // background+text tone
  emoji: string;
};

export const ACCOUNT_META: Record<Exclude<AccountType, "citizen">, Meta> = {
  government:   { label: "Verified Government Representative", short: "Government",   icon: Landmark,       emoji: "🏛", className: "bg-primary/15 text-primary" },
  municipality: { label: "Verified Municipality",              short: "Municipality", icon: Building2,      emoji: "🏙", className: "bg-primary/15 text-primary" },
  ngo:          { label: "Verified NGO",                       short: "NGO",          icon: Globe2,         emoji: "🌍", className: "bg-success/15 text-success" },
  journalist:   { label: "Verified Journalist",                short: "Journalist",   icon: Newspaper,      emoji: "📰", className: "bg-accent text-accent-foreground" },
  emergency:    { label: "Emergency Services",                 short: "Emergency",    icon: Siren,          emoji: "🚨", className: "bg-emergency/15 text-emergency" },
  business:     { label: "Verified Business",                  short: "Business",     icon: Briefcase,      emoji: "💼", className: "bg-warning/15 text-warning" },
  research:     { label: "Verified Research Org",              short: "Research",     icon: GraduationCap,  emoji: "🎓", className: "bg-secondary text-secondary-foreground" },
};

export function isOfficial(t?: AccountType | null) {
  return !!t && t !== "citizen";
}

export function accountTypeLabel(t?: AccountType | null) {
  if (!t || t === "citizen") return "Citizen";
  return ACCOUNT_META[t].label;
}

/** Compact verified/official chip shown next to a username. */
export function AccountBadge({
  accountType, verified, isFounder, size = "sm", showLabel = false, className,
}: {
  accountType?: AccountType | null;
  verified?: boolean | null;
  isFounder?: boolean | null;
  size?: "xs" | "sm";
  showLabel?: boolean;
  className?: string;
}) {
  const isOff = isOfficial(accountType);
  if (!isFounder && !isOff && !verified) return null;

  if (isFounder) {
    return (
      <span
        title="Founder of Xamseen"
        className={cn(
          "inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 px-1.5 font-semibold text-white shadow-sm",
          size === "xs" ? "h-4 text-[10px]" : "h-5 text-[11px]",
          className,
        )}
      >
        <Crown className={size === "xs" ? "h-2.5 w-2.5" : "h-3 w-3"} />
        {showLabel ? <span>Founder</span> : null}
      </span>
    );
  }


  if (isOff) {
    const meta = ACCOUNT_META[accountType as Exclude<AccountType, "citizen">];
    const Icon = meta.icon;
    return (
      <span
        title={meta.label}
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-1.5 font-medium",
          meta.className,
          size === "xs" ? "h-4 text-[10px]" : "h-5 text-[11px]",
          className,
        )}
      >
        <Icon className={size === "xs" ? "h-2.5 w-2.5" : "h-3 w-3"} />
        {showLabel && <span>{meta.short}</span>}
      </span>
    );
  }

  return (
    <span
      title="Verified contributor"
      className={cn(
        "inline-flex items-center justify-center rounded-full bg-primary/15 text-primary",
        size === "xs" ? "h-4 w-4" : "h-5 w-5",
        className,
      )}
    >
      <BadgeCheck className={size === "xs" ? "h-3 w-3" : "h-3.5 w-3.5"} />
    </span>
  );
}
